import { Link } from "react-router-dom";
import { Mail, CalendarDays, MapPin } from "lucide-react";
import { NAV, slug, CALENDLY_URL, CONTACT_EMAIL, PODCAST_URL } from "@/data/nav";
import LOCATIONS from "@/data/locations.json";
import { comingSoon } from "@/lib/site";
import { LogoMark } from "./Logo";
import { Reveal } from "./Motion";

const linkCls = "text-left text-[14px] text-slate-300 transition-colors hover:text-white";

const FooterLink = ({ it, prefix }) => {
  const tid = `footer-link-${prefix ? `${prefix}-` : ""}${slug(it.label)}`;
  if (it.href) return <a href={it.href} target="_blank" rel="noopener noreferrer" data-testid={tid} className={linkCls}>{it.label} ↗</a>;
  if (it.to) return <Link to={it.to} data-testid={tid} className={linkCls}>{it.label}</Link>;
  return <button type="button" data-testid={tid} onClick={() => comingSoon(it.label)} className={linkCls}>{it.label}</button>;
};

const Col = ({ label, items, prefix, cols }) => (
  <div>
    <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-violet-300">{label}</p>
    <ul className={`mt-5 ${cols ? "grid gap-x-8 gap-y-2.5 sm:grid-cols-2" : "space-y-2.5"}`}>
      {items.map((it) => <li key={it.label}><FooterLink it={it} prefix={prefix} /></li>)}
    </ul>
  </div>
);

const COMPANY = [
  { label: "Results", to: "/results" },
  { label: "Insights", to: "/insights" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
  { label: "Podcast", href: PODCAST_URL },
  { label: "Perfect Digitals Studio", to: "/studio" },
  { label: "Privacy Policy", to: "/privacy-policy" },
];
const LOCS = LOCATIONS.map((l) => ({ label: `SEO Agency ${l.city}`, to: `/${l.slug}` }));

const CONTACT = [
  { icon: Mail, label: "Email", lines: [CONTACT_EMAIL], href: `mailto:${CONTACT_EMAIL}` },
  { icon: CalendarDays, label: "Book a Meeting", lines: ["30-minute call with our team"], href: CALENDLY_URL },
];
export const OFFICES = [
  { label: "UK Office", lines: ["Dalton House", "60 Windsor Avenue", "London, SW19 2RR UK"] },
  { label: "Ireland Office", lines: ["51 Bracken Road, Sandyford", "Dublin 18, D18 CV48", "Ireland"] },
];

export const ContactCard = ({ icon: Icon, label, lines, i, href }) => {
  const body = (
    <>
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-violet-500/15 text-violet-300"><Icon className="h-4 w-4" /></span>
      <span className="min-w-0">
        <span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">{label}</span>
        {lines.map((l) => <span key={l} className="block break-words font-display text-[17px] leading-snug text-white">{l}</span>)}
      </span>
    </>
  );
  const cls = "flex h-full items-start gap-5 rounded-2xl border border-white/10 bg-white/[0.03] p-5 transition-colors duration-300";
  return (
    <Reveal delay={i * 0.06} data-testid={`footer-contact-${slug(label)}`}>
      {href
        ? <a href={href} target={href.startsWith("mailto") ? undefined : "_blank"} rel="noopener noreferrer" className={`${cls} hover:border-violet-400 hover:bg-white/[0.06]`}>{body}</a>
        : <div className={cls}>{body}</div>}
    </Reveal>
  );
};

export const Footer = () => (
  <footer id="contact" data-testid="site-footer" className="grain overflow-hidden bg-ink text-white">
    <div className="wrap pt-20 sm:pt-28">
      <div className="grid gap-12 border-b border-white/10 pb-16 lg:grid-cols-12">
        <Reveal className="lg:col-span-5">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-violet-300">Contact</p>
          <h2 className="mt-5 font-display text-4xl font-medium leading-[1.02] sm:text-5xl">
            Speak to a <span className="font-serif-i text-gradient">Search Specialist.</span>
          </h2>
          <p className="mt-5 max-w-md text-slate-300">Book a meeting or get in touch to talk about your visibility across Google and AI search.</p>
        </Reveal>
        <div className="grid gap-3 sm:grid-cols-2 lg:col-span-7 lg:pl-10">
          {CONTACT.map((c, i) => <ContactCard key={c.label} {...c} i={i} />)}
          {OFFICES.map((c, i) => <ContactCard key={c.label} icon={MapPin} {...c} i={i + 2} />)}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-10 py-16 lg:grid-cols-5">
        {NAV.filter((n) => n.items).map((g) => <Col key={g.id} label={g.label} items={g.items} />)}
        <Col label="Company" items={COMPANY} prefix="company" />
        <div className="col-span-2">
          <Col label="Locations" items={LOCS} cols />
        </div>
      </div>
      <div className="flex flex-col gap-4 border-t border-white/10 py-8 text-[13px] text-slate-400 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3"><LogoMark className="h-6 w-6" /> © {new Date().getFullYear()} Perfect Digitals UK. SEO & AI Search Visibility Specialists.</div>
        <div className="flex gap-6">
          <Link to="/privacy-policy" data-testid="footer-privacy-link" className="hover:text-white">Privacy Policy</Link>
          <Link to="/studio" data-testid="footer-studio-link" className="hover:text-white">Perfect Digitals Studio</Link>
        </div>
      </div>
    </div>
    <div aria-hidden="true" className="select-none whitespace-nowrap text-center font-display text-[15.5vw] font-semibold leading-[0.85] pb-[1vw] tracking-tighter outline-text">
      Perfect Digitals
    </div>
  </footer>
);
